import type { Maybe } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';

import type { ICrossWindowLogEntry } from './shared/crossWindowMessaging';

// TODO (?) make misc changes in @vimeo/logger (e.g. 'onLog' callback or change some private methods to protected) to simplify this
// TODO (?) stop sending logs to cross window transport after N failed attempts in a row
export class IntegrationLogger extends Logger {
  private static crossWindowLogTransport: Maybe<(logEntry: ICrossWindowLogEntry) => void>;

  public static setCrossWindowLogTransport(
    crossWindowLogTransport: (logEntry: ICrossWindowLogEntry) => void,
  ): void {
    IntegrationLogger.crossWindowLogTransport = crossWindowLogTransport;
  }

  public static create(
    topic: string,
    useCrossWindowLogTransport: boolean = true,
  ): IntegrationLogger {
    if (useCrossWindowLogTransport) {
      return new IntegrationLogger(topic, (logEntry: ICrossWindowLogEntry) => {
        try {
          IntegrationLogger.crossWindowLogTransport?.(logEntry);
        } catch {
          // fail silently
        }
      });
    }
    return new IntegrationLogger(topic);
  }

  private static stringifyDataSafe(data: ICrossWindowLogEntry['data']): string {
    try {
      return JSON.stringify(data);
    } catch {
      return String(data);
    }
  }

  private readonly topic: string;
  private readonly crossWindowLogTransport: Maybe<(entry: ICrossWindowLogEntry) => void>;

  private constructor(
    topic: string,
    crossWindowLogTransport?: Maybe<(entry: ICrossWindowLogEntry) => void>,
  ) {
    super(topic, true);
    this.topic = topic;
    this.crossWindowLogTransport = crossWindowLogTransport;
  }

  public override debug(...args: [message: string, data?: unknown]): void {
    super.debug(...args);
    this.sendCrossWindowMessage('debug', ...args);
  }

  public override info(...args: [message: string, data?: unknown]): void {
    super.info(...args);
    this.sendCrossWindowMessage('info', ...args);
  }

  public override warn(...args: [message: string, data?: unknown]): void {
    super.warn(...args);
    this.sendCrossWindowMessage('warn', ...args);
  }

  public override error(...args: [message: string, data?: unknown]): void {
    super.error(...args);
    this.sendCrossWindowMessage('error', ...args);
  }

  private sendCrossWindowMessage(
    level: ICrossWindowLogEntry['level'],
    message: ICrossWindowLogEntry['message'],
    data?: ICrossWindowLogEntry['data'],
  ): void {
    this.crossWindowLogTransport?.({
      level,
      message,
      timestamp: Date.now(),
      topic: this.topic,
      data: IntegrationLogger.stringifyDataSafe(data),
    });
  }
}
